import {
  type Chain,
  mainnet,
  polygon,
  arbitrum,
  avalanche,
  base,
  bsc,
  optimism,
  scroll,
  foundry,
} from 'wagmi/chains';

import { ClientConfigurationError } from '@/lib/errors/client';

import { isTest } from '@/utils/environment';

export const CHAIN_IDS = {
  MAINNET: mainnet.id,
  BASE: base.id,
  BSC: bsc.id,
  MATIC: polygon.id,
  AVALANCHE: avalanche.id,
  ARBITRUM: arbitrum.id,
  OPTIMISM: optimism.id,
  FOUNDRY: foundry.id,
  SCROLL: scroll.id,
} as const;

// source: https://dune.com/queries/3285438/5499741
export const BLOCK_CONFIRMATION_TIME_IN_SECONDS: {
  [key: keyof typeof SUPPORTED_CHAINS_BY_CHAIN_ID]: number;
} = {
  [CHAIN_IDS.MAINNET]: 12,
  [CHAIN_IDS.BSC]: 3,
  [CHAIN_IDS.MATIC]: 2.3,
  [CHAIN_IDS.AVALANCHE]: 2,
  [CHAIN_IDS.ARBITRUM]: 0.26,
  [CHAIN_IDS.OPTIMISM]: 2,
  [CHAIN_IDS.BASE]: 2,
  [CHAIN_IDS.SCROLL]: 2.9,
};

export const NATIVE_TOKEN_SYMBOL_PER_CHAIN: { [key: number]: string } = {
  [CHAIN_IDS.MAINNET]: mainnet.nativeCurrency.symbol,
  [CHAIN_IDS.BASE]: base.nativeCurrency.symbol,
  [CHAIN_IDS.BSC]: bsc.nativeCurrency.symbol,
  [CHAIN_IDS.MATIC]: polygon.nativeCurrency.symbol,
  [CHAIN_IDS.AVALANCHE]: avalanche.nativeCurrency.symbol,
  [CHAIN_IDS.ARBITRUM]: arbitrum.nativeCurrency.symbol,
  [CHAIN_IDS.OPTIMISM]: optimism.nativeCurrency.symbol,
  [CHAIN_IDS.SCROLL]: scroll.nativeCurrency.symbol,
};

export const SUPPORTED_CHAINS: Chain[] = [
  mainnet,
  polygon,
  arbitrum,
  base,
  optimism,
  bsc,
  avalanche,
  scroll,
  ...(isTest ? [foundry] : []),
];

export const LIMIT_ORDER_SUPPORTED_CHAINS: Chain[] = [
  mainnet,
  polygon,
  bsc,
  ...(isTest ? [foundry] : []),
];

// Shall we integrate cross-chain scroll in a separate PR (on top of this current PR)?
export const CROSS_CHAIN_SUPPORTED_CHAINS: Chain[] = [
  mainnet,
  polygon,
  arbitrum,
  base,
  optimism,
  bsc,
  avalanche,
  scroll,
  ...(isTest ? [foundry] : []),
];

export const SUPPORTED_CHAINS_BY_CHAIN_ID = SUPPORTED_CHAINS.reduce(
  (memo, chain: Chain) => {
    memo[chain.id] = chain;
    return memo;
  },
  {} as { [key: number]: Chain },
);

export type SupportedChainIDs = keyof typeof SUPPORTED_CHAINS_BY_CHAIN_ID;

export const CHAIN_IDS_SUPPORT_EIP1559: number[] = [
  CHAIN_IDS.BASE,
  CHAIN_IDS.MAINNET,
  CHAIN_IDS.MATIC,
  CHAIN_IDS.OPTIMISM,
  CHAIN_IDS.AVALANCHE,
  CHAIN_IDS.SCROLL,
  ...(isTest ? [CHAIN_IDS.FOUNDRY] : []),
];

export const CHAIN_IDS_SUPPORT_GASLESS: number[] = [
  CHAIN_IDS.MAINNET,
  CHAIN_IDS.MATIC,
  CHAIN_IDS.ARBITRUM,
  CHAIN_IDS.BASE,
  CHAIN_IDS.OPTIMISM,
  CHAIN_IDS.SCROLL,
];

export const SUPPORTED_CHAIN_IDS = SUPPORTED_CHAINS.map((chain) => chain.id);
export const SUPPORTED_CHAIN_IDS_LIMIT_ORDERS = LIMIT_ORDER_SUPPORTED_CHAINS.map(
  (chain) => chain.id,
);

/**
 * Chains that support bridging.
 *
 * @remarks
 *
 * All chains support bridging except for Foundry.
 */
export const SUPPORTED_CHAIN_IDS_CROSS_CHAIN = CROSS_CHAIN_SUPPORTED_CHAINS.map((c) => c.id);

export const NETWORK_NAME_PER_CHAIN_ID = SUPPORTED_CHAINS.reduce<{
  [key: number]: string;
}>((chainNameByChainId, chain: Chain) => {
  chainNameByChainId[chain.id] =
    chain.id === CHAIN_IDS.BSC
      ? 'BNB Chain' // override BSC name to be "BNB Chain"
      : chain.name;

  return chainNameByChainId;
}, {});

/*
The three reducers defined below are used to power the URL strucutre.
Due to Wagmi's config, we need to define the network name for Mainnet to 'ethereum'
since Wagmi has it defined as 'homestead'. We also are doing a custom mapping for
polygon, since Wagmi's config names the network 'matic'.
*/

/** A mapping from a human readable chain name to chainId. */
export const CHAIN_ID_PER_CHAIN_NAME = SUPPORTED_CHAINS.reduce<{ [key: string]: number }>(
  (chainIdByChainName, chain: Chain) => {
    chainIdByChainName[chain.name] = chain.id;

    if (chain.id === CHAIN_IDS.BSC) {
      chainIdByChainName['BNB Chain'] = chain.id;
    }

    return chainIdByChainName;
  },
  {},
);

/**
 *
 * Mapping from network name to chain id.
 *
 * @deprecated The `network` property of viem Chains is deprecated, use {@link CHAIN_ID_PER_CHAIN_NAME} instead.
 */
export const CHAIN_ID_PER_NETWORK_NAME = SUPPORTED_CHAINS.reduce<{ [key: string]: number }>(
  (chainIdByChainName, chain: Chain) => {
    switch (chain.id) {
      case CHAIN_IDS.MAINNET:
        chainIdByChainName['ethereum'] = CHAIN_IDS.MAINNET;
        break;
      case CHAIN_IDS.MATIC:
        chainIdByChainName['polygon'] = CHAIN_IDS.MATIC;
        break;
      case CHAIN_IDS.BSC:
        chainIdByChainName['bsc'] = CHAIN_IDS.BSC;
        break;
      case CHAIN_IDS.ARBITRUM:
        chainIdByChainName['arbitrum'] = CHAIN_IDS.ARBITRUM;
        break;
      case CHAIN_IDS.OPTIMISM:
        chainIdByChainName['optimism'] = CHAIN_IDS.OPTIMISM;
        break;
      case CHAIN_IDS.AVALANCHE:
        chainIdByChainName['avalanche'] = CHAIN_IDS.AVALANCHE;
        break;
      case CHAIN_IDS.BASE:
        chainIdByChainName['base'] = CHAIN_IDS.BASE;
        break;
      case CHAIN_IDS.SCROLL:
        chainIdByChainName['scroll'] = CHAIN_IDS.SCROLL;
        break;
      case CHAIN_IDS.FOUNDRY:
        chainIdByChainName['foundry'] = CHAIN_IDS.FOUNDRY;
        break;
      default:
        throw new ClientConfigurationError('Supported chain missing slug');
    }

    return chainIdByChainName;
  },
  {},
);

/**
 * The names of chains used by the 0x Data API.
 */
export const DATA_API_CHAIN_NAMES: Record<number, string> = {
  [CHAIN_IDS.MAINNET]: 'Ethereum',
  [CHAIN_IDS.OPTIMISM]: 'Optimism',
  [CHAIN_IDS.BASE]: 'Base',
  [CHAIN_IDS.BSC]: 'BSC',
  [CHAIN_IDS.MATIC]: 'Polygon',
  [CHAIN_IDS.ARBITRUM]: 'Arbitrum',
  [CHAIN_IDS.AVALANCHE]: 'Avalanche',
  [CHAIN_IDS.SCROLL]: 'Scroll',
};

export const NETWORK_NAME_FOR_URLPATH_PER_CHAIN_ID = Object.entries(
  CHAIN_ID_PER_NETWORK_NAME,
).reduce<{
  [key: number]: string;
}>((chainNameByChainId, [slug, id]) => {
  chainNameByChainId[id] = slug;
  return chainNameByChainId;
}, {});
